<template>
  <div class="confirm-page">
    <navbar />

    <main class="confirm-main">
      <section class="confirm-panel" aria-live="polite">
        <div class="confirm-panel-topline"></div>

        <div class="confirm-icon" aria-hidden="true">
          <i class="fas fa-envelope-open-text"></i>
        </div>

        <div class="confirm-kicker">Email confirmation</div>
        <h1>Check your email</h1>
        <p class="confirm-copy">
          We created your GixCore account. Confirm your email before signing in so your builds,
          profile, and saved parts stay attached to the right account.
        </p>

        <div class="confirm-destination">
          <span>Confirmation sent to</span>
          <strong>{{ emailLabel }}</strong>
        </div>

        <div class="confirm-alert" :class="{ 'is-warning': emailSent === false }">
          <span class="confirm-alert-title">{{ emailSent === false ? 'Account created, email not sent.' : 'Open the newest confirmation link.' }}</span>
          <span>
            {{ emailSent === false
              ? 'We could not send the confirmation email automatically. Try resend below.'
              : 'The link activates your account and returns you to the login page.'
            }}
          </span>
        </div>

        <div class="confirm-steps">
          <div>
            <span>1</span>
            <strong>Open inbox</strong>
            <p>Check the email address used during registration.</p>
          </div>
          <div>
            <span>2</span>
            <strong>Confirm account</strong>
            <p>Use the newest GixCore confirmation link.</p>
          </div>
          <div>
            <span>3</span>
            <strong>Sign in</strong>
            <p>Return to GixCore and continue your build.</p>
          </div>
        </div>

        <div class="resend-card">
          <label for="resend-email">Confirmation email</label>
          <div class="resend-row">
            <input
              id="resend-email"
              v-model="resendEmail"
              type="email"
              autocomplete="email"
              placeholder="you@example.com"
            >
            <button type="button" :disabled="resendLoading || resendCooldownSeconds > 0" @click="resendConfirmation">
              {{ resendButtonLabel }}
            </button>
          </div>
          <p v-if="resendMessage" class="resend-message" :class="'is-' + resendMessage.type">
            {{ resendMessage.text }}
          </p>
        </div>

        <div class="confirm-actions">
          <router-link class="confirm-link primary" to="/users/login">Go to login</router-link>
          <router-link class="confirm-link" to="/users/register">Use another email</router-link>
        </div>
      </section>
    </main>
  </div>
</template>

<script>
import navbar from '../navbar.vue'
import {
  confirmationResendNotice,
  cooldownButtonLabel,
  parseConfirmationResendResult,
} from '../../emailConfirmation'

export default {
  name: 'RegisterSuccess',
  components: {
    navbar,
  },
  data() {
    return {
      resendEmail: '',
      emailSent: true,
      resendLoading: false,
      resendMessage: null,
      resendCooldownSeconds: 0,
      resendCooldownTimer: null,
    }
  },
  computed: {
    emailLabel() {
      return this.resendEmail || 'the email address you registered with'
    },
    resendButtonLabel() {
      if (this.resendLoading) {
        return 'Sending...'
      }
      if (this.resendCooldownSeconds > 0) {
        return cooldownButtonLabel(this.resendCooldownSeconds)
      }
      return 'Resend'
    },
  },
  created() {
    this.resendEmail = this.$store.getters.pendingConfirmationEmail ||
      sessionStorage.getItem('gixcore_pending_confirmation_email') ||
      ''

    const sentFromRoute = this.$route && this.$route.query ? this.$route.query.sent : ''
    const sentFromStorage = sessionStorage.getItem('gixcore_pending_confirmation_email_sent')
    const sent = sentFromRoute || sentFromStorage
    this.emailSent = sent === '0' ? false : true
  },
  beforeDestroy() {
    this.stopResendCooldown()
  },
  methods: {
    resendConfirmation() {
      if (this.resendCooldownSeconds > 0) {
        return
      }

      if (!this.resendEmail) {
        this.resendMessage = {
          type: 'warning',
          text: 'Enter the email address you registered with, then try again.',
        }
        return
      }

      this.resendLoading = true

      this.$store.dispatch('resendConfirmation', { email: this.resendEmail })
        .then((response) => {
          this.resendLoading = false
          this.applyResendOutcome(parseConfirmationResendResult(response))
        })
        .catch((err) => {
          this.resendLoading = false
          this.applyResendOutcome(parseConfirmationResendResult(err))
        })
    },
    applyResendOutcome(outcome) {
      const notice = confirmationResendNotice(outcome)

      if (notice.emailSent) {
        this.emailSent = true
        sessionStorage.setItem('gixcore_pending_confirmation_email_sent', '1')
      }

      this.resendMessage = {
        type: notice.type,
        text: notice.text,
      }

      if (notice.cooldownSeconds > 0) {
        this.startResendCooldown(notice.cooldownSeconds)
      } else {
        this.stopResendCooldown()
      }
    },
    startResendCooldown(seconds) {
      this.stopResendCooldown()
      this.resendCooldownSeconds = Math.max(1, Math.ceil(Number(seconds) || 60))
      this.resendCooldownTimer = window.setInterval(() => {
        if (this.resendCooldownSeconds <= 1) {
          this.stopResendCooldown()
          return
        }
        this.resendCooldownSeconds -= 1
      }, 1000)
    },
    stopResendCooldown() {
      if (this.resendCooldownTimer) {
        window.clearInterval(this.resendCooldownTimer)
        this.resendCooldownTimer = null
      }
      this.resendCooldownSeconds = 0
    },
  },
}
</script>

<style scoped>
@import '../../assets/css/custom.css';

.confirm-page {
	position: relative;
	min-height: 100vh;
	overflow: hidden;
	background: #080a0f;
	color: #cfd2da;
}

.confirm-page::before {
	position: fixed;
	inset: 0;
	z-index: 0;
	background:
		linear-gradient(90deg, rgba(4, 6, 10, 0.88) 0%, rgba(6, 8, 12, 0.76) 48%, rgba(4, 6, 10, 0.94) 100%),
		linear-gradient(180deg, rgba(8, 10, 15, 0.15) 0%, rgba(8, 10, 15, 0.94) 100%),
		url('~@/assets/img/builder-bg.png');
	background-position: center;
	background-repeat: no-repeat;
	background-size: cover;
	content: "";
}

.confirm-page::after {
	position: fixed;
	right: 0;
	bottom: 0;
	left: 0;
	z-index: 0;
	height: 34vh;
	background: linear-gradient(180deg, rgba(8, 10, 15, 0), rgba(8, 10, 15, 0.92));
	content: "";
	pointer-events: none;
}

.confirm-main {
	position: relative;
	z-index: 1;
	display: flex;
	align-items: center;
	justify-content: center;
	min-height: calc(100vh - 72px);
	padding: 1.25rem 1.25rem 4.5rem;
}

.confirm-panel {
	position: relative;
	width: min(100%, 590px);
	padding: 1.55rem;
	border: 1px solid rgba(67, 72, 87, 0.95);
	border-radius: 6px;
	background:
		linear-gradient(180deg, rgba(25, 27, 32, 0.94), rgba(13, 15, 22, 0.96)),
		rgba(25, 27, 32, 0.94);
	box-shadow:
		0 22px 70px rgba(0, 0, 0, 0.42),
		inset 0 0 0 1px rgba(255, 255, 255, 0.025);
}

.confirm-panel::before {
	position: absolute;
	inset: 9px;
	z-index: -1;
	border: 1px solid rgba(25, 151, 198, 0.09);
	border-radius: 4px;
	content: "";
}

.confirm-panel-topline {
	position: absolute;
	top: -1px;
	right: 12px;
	left: 12px;
	height: 1px;
	background: linear-gradient(90deg, rgba(25, 151, 198, 0), rgba(25, 151, 198, 0.85), rgba(25, 151, 198, 0));
}

.confirm-icon {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	width: 46px;
	height: 46px;
	margin-bottom: 1rem;
	border: 1px solid rgba(25, 151, 198, 0.62);
	border-radius: 6px;
	background: rgba(25, 151, 198, 0.1);
	color: #16b4ed;
	font-size: 20px;
	box-shadow: inset 0 0 20px rgba(25, 151, 198, 0.08);
}

.confirm-kicker {
	margin-bottom: 0.45rem;
	color: #1997c6;
	font-size: 12px;
	font-weight: 700;
	letter-spacing: 0.9px;
	line-height: 1;
	text-transform: uppercase;
}

.confirm-panel h1 {
	margin: 0;
	color: #d8dce4;
	font-size: 30px;
	font-weight: 500;
	letter-spacing: 0;
	line-height: 1.15;
}

.confirm-copy {
	max-width: 500px;
	margin: 0.75rem 0 1rem;
	color: #9da4b5;
	font-size: 14px;
	line-height: 1.5;
}

.confirm-destination {
	display: grid;
	gap: 0.25rem;
	margin-bottom: 1rem;
	padding: 0.75rem 0.85rem;
	border: 1px solid rgba(67, 72, 87, 0.8);
	border-radius: 6px;
	background: rgba(7, 10, 16, 0.5);
}

.confirm-destination span {
	color: #9198aa;
	font-size: 11px;
	font-weight: 800;
	letter-spacing: 0.65px;
	text-transform: uppercase;
}

.confirm-destination strong {
	color: #e5e8ef;
	font-size: 15px;
	word-break: break-word;
}

.confirm-alert {
	display: grid;
	gap: 0.2rem;
	margin-bottom: 1rem;
	padding: 0.75rem 0.85rem;
	border: 1px solid rgba(25, 151, 198, 0.48);
	border-radius: 6px;
	background: rgba(25, 151, 198, 0.08);
	color: #bcd5df;
	font-size: 13px;
	line-height: 1.4;
}

.confirm-alert-title {
	color: #dceaf0;
	font-weight: 800;
}

.confirm-alert.is-warning {
	border-color: rgba(231, 195, 82, 0.5);
	background: rgba(231, 195, 82, 0.08);
	color: #e7d79e;
}

.confirm-alert.is-warning .confirm-alert-title {
	color: #f1dc83;
}

.confirm-steps {
	display: grid;
	grid-template-columns: repeat(3, 1fr);
	gap: 0.75rem;
	margin-bottom: 1rem;
}

.confirm-steps > div {
	min-height: 118px;
	padding: 0.8rem;
	border: 1px solid rgba(67, 72, 87, 0.78);
	border-radius: 6px;
	background: rgba(8, 10, 15, 0.48);
}

.confirm-steps span {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	width: 24px;
	height: 24px;
	margin-bottom: 0.55rem;
	border: 1px solid rgba(25, 151, 198, 0.7);
	border-radius: 5px;
	color: #16b4ed;
	font-size: 12px;
	font-weight: 800;
}

.confirm-steps strong {
	display: block;
	color: #d8dce4;
	font-size: 13px;
}

.confirm-steps p {
	margin: 0.35rem 0 0;
	color: #9198aa;
	font-size: 12px;
	line-height: 1.45;
}

.resend-card {
	padding: 0.85rem;
	border: 1px solid rgba(67, 72, 87, 0.82);
	border-radius: 6px;
	background: rgba(7, 10, 16, 0.58);
}

.resend-card label {
	display: block;
	margin-bottom: 0.45rem;
	color: #b2b3b4;
	font-size: 12px;
	font-weight: 700;
}

.resend-row {
	display: grid;
	grid-template-columns: 1fr 116px;
	gap: 0.65rem;
}

.resend-row input {
	width: 100%;
	min-height: 40px;
	padding: 0.6rem 0.75rem;
	border: 1px solid rgba(67, 72, 87, 0.96);
	border-radius: 6px;
	outline: none;
	background: rgba(6, 8, 12, 0.78);
	color: #e5e8ef;
	font-size: 14px;
}

.resend-row input:focus {
	border-color: #1997c6;
	box-shadow: 0 0 0 3px rgba(25, 151, 198, 0.11);
}

.resend-row button,
.confirm-link {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	min-height: 40px;
	border: 1px solid rgba(25, 151, 198, 0.9);
	border-radius: 6px;
	background: rgba(8, 20, 30, 0.72);
	color: #16b4ed;
	cursor: pointer;
	font-size: 12px;
	font-weight: 800;
	letter-spacing: 0.45px;
	text-transform: uppercase;
	text-decoration: none;
	transition: border-color 160ms ease, background 160ms ease, color 160ms ease;
}

.resend-row button:hover,
.resend-row button:focus,
.confirm-link:hover,
.confirm-link:focus {
	background: rgba(25, 151, 198, 0.16);
	color: #dff8ff;
	text-decoration: none;
}

.resend-row button:disabled {
	border-color: rgba(67, 72, 87, 0.96);
	background: rgba(67, 72, 87, 0.26);
	color: #9198aa;
	cursor: not-allowed;
}

.confirm-link.primary {
	padding: 0 1rem;
	background: linear-gradient(180deg, rgba(25, 151, 198, 0.34), rgba(12, 66, 88, 0.64));
	color: #f4f8fb;
}

.resend-message {
	margin: 0.65rem 0 0;
	font-size: 12px;
	line-height: 1.4;
}

.resend-message.is-success {
	color: #9ff0d8;
}

.resend-message.is-warning {
	color: #f1dc83;
}

.resend-message.is-error {
	color: #ff9aa5;
}

.resend-message.is-info {
	color: #bcd5df;
}

.confirm-actions {
	display: flex;
	flex-wrap: wrap;
	gap: 0.75rem;
	margin-top: 1rem;
}

.confirm-actions .confirm-link {
	min-width: 142px;
	padding: 0 0.9rem;
}

@media screen and (max-width: 700px) {
	.confirm-main {
		align-items: stretch;
		padding: 0.65rem 1rem 1rem;
	}

	.confirm-panel {
		width: 100%;
		padding: 1.15rem;
	}

	.confirm-panel h1 {
		font-size: 25px;
	}

	.confirm-steps,
	.resend-row {
		grid-template-columns: 1fr;
	}
}
</style>
